import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Button,
  Link,
  VStack
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

const OauthCallbackFacebook = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('cliente');
  const [displayName, setDisplayName] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('/registro-conductor');

  useEffect(() => {
    if (status !== 'loading') return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setError(t('oauth_facebook_error_denied'));
      return;
    }

    if (!code) {
      setStatus('error');
      setError(t('oauth_facebook_error_no_code'));
      return;
    }

    const callbackRequestKey = `facebook:${code}:${state || 'no-state'}`;
    const sessionLockKey = `oauth_callback_lock:${callbackRequestKey}`;
    if (typeof window !== 'undefined') {
      if (window.__overtaxiFacebookOauthCallbackKey === callbackRequestKey) {
        return;
      }

      const sessionLock = window.sessionStorage?.getItem(sessionLockKey);
      if (sessionLock === 'inflight' || sessionLock === 'done') {
        return;
      }

      window.__overtaxiFacebookOauthCallbackKey = callbackRequestKey;
      window.sessionStorage?.setItem(sessionLockKey, 'inflight');
    }

    const API_BASE_URL = getApiBaseUrl();
    const queryParams = new URLSearchParams({ code });
    if (state) {
      queryParams.set('state', state);
    }
    const query = queryParams.toString();

    fetch(`${API_BASE_URL}/api/auth/facebook/callback?${query}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || t('oauth_facebook_error_desconocido'));
        }
        return data;
      })
      .then((data) => {
        if (typeof window !== 'undefined') {
          window.sessionStorage?.setItem(sessionLockKey, 'done');
        }

        if (data.status === 'needs_email') {
          setPendingToken(data.pending_token);
          setUserType(data.user_type || 'cliente');
          setDisplayName(data.display_name || '');
          setStatus('needs_email');
          return;
        }

        if (data.status === 'needs_registration') {
          setUserType(data.user_type || 'conductor');
          setEmail(data.email || '');
          setRegistrationUrl(data.registration_url || '/registro-conductor');
          setStatus('needs_registration');
          return;
        }

        login(data);
        navigate(data.user_type === 'conductor' ? '/conductor' : '/cliente');
      })
      .catch((err) => {
        if (typeof window !== 'undefined') {
          window.__overtaxiFacebookOauthCallbackKey = null;
          window.sessionStorage?.removeItem(sessionLockKey);
        }
        setStatus('error');
        setError(err.message);
      });
  }, [login, navigate, searchParams, status, t]);

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/auth/facebook/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pending_token: pendingToken,
          email
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || t('oauth_facebook_error_desconocido'));
      }

      if (data.status === 'needs_registration') {
        setUserType(data.user_type || 'conductor');
        setEmail(data.email || email);
        setRegistrationUrl(data.registration_url || '/registro-conductor');
        setStatus('needs_registration');
        return;
      }

      login(data);
      navigate(data.user_type === 'conductor' ? '/conductor' : '/cliente');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box p={4} maxW="420px" mx="auto" mt={10}>
      <Heading as="h2" size="lg" mb={6} textAlign="center">
        {t('oauth_facebook_titulo')}
      </Heading>

      {status === 'loading' && (
        <Box textAlign="center">
          <Spinner size="xl" />
          <Text mt={4}>{t('oauth_facebook_procesando')}</Text>
        </Box>
      )}

      {status === 'error' && (
        <Alert status="error" mt={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {status === 'needs_email' && (
        <VStack spacing={4} align="stretch">
          <Text>{t('oauth_facebook_email_prompt', { name: displayName })}</Text>
          <Box as="form" onSubmit={handleEmailSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl id="email" isRequired>
                <FormLabel>{t('oauth_facebook_email_label')}</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('oauth_facebook_email_placeholder')}
                />
              </FormControl>
              <Button type="submit" colorScheme="gray">
                {t('oauth_facebook_email_submit')}
              </Button>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
            </VStack>
          </Box>
        </VStack>
      )}

      {status === 'needs_registration' && (
        <VStack spacing={4} align="stretch">
          <Text>{t('oauth_facebook_register_prompt', { userType })}</Text>
          <Link color="gray.600" href={registrationUrl}>
            {t('oauth_facebook_register_link')}
          </Link>
        </VStack>
      )}
    </Box>
  );
};

export default OauthCallbackFacebook;
